/**
 * Minimal phone number utilities (country list, dial codes, parse, validate).
 * Replaces libphonenumber-js in the widget to avoid ~365KB bundle cost.
 * Data extracted from libphonenumber-js metadata; validation is E.164 only.
 */

import countryDialCodes from './phone-country-dial-codes.json';

const COUNTRY_DIAL = countryDialCodes as Record<string, string>;

// One country per dial code (for parsing); dial codes sorted by length descending for longest-match
const DIAL_TO_COUNTRY: Record<string, string> = {};
const DIAL_CODES_ORDERED: string[] = [];
for (const [country, dial] of Object.entries(COUNTRY_DIAL)) {
  if (!DIAL_TO_COUNTRY[dial]) {
    DIAL_TO_COUNTRY[dial] = country;
  }
}
for (const dial of Object.keys(DIAL_TO_COUNTRY)) {
  DIAL_CODES_ORDERED.push(dial);
}
DIAL_CODES_ORDERED.sort((a, b) => b.length - a.length);

export function getCountries(): string[] {
  return Object.keys(COUNTRY_DIAL);
}

export function getCountryCallingCode(countryCode: string): string {
  return COUNTRY_DIAL[countryCode] ?? '';
}

export interface ParsedPhoneNumber {
  country: string;
  nationalNumber: string;
  number: string;
}

/**
 * Parse a phone number string (E.164 or digits with optional leading +).
 * Returns null if the number cannot be matched to a known country dial code.
 */
export function parsePhoneNumber(
  value: string,
  _defaultCountry?: string
): ParsedPhoneNumber | null {
  const digits = value.replace(/\D/g, '');
  // Minimum 7 digits: shortest valid E.164 = 1-digit country code + 6-digit subscriber
  if (digits.length < 7) {
    return null;
  }
  for (const dial of DIAL_CODES_ORDERED) {
    if (digits.startsWith(dial)) {
      const country = DIAL_TO_COUNTRY[dial];
      const nationalNumber = digits.slice(dial.length);
      if (nationalNumber.length < 4) {
        continue;
      }
      return {
        country,
        nationalNumber,
        number: `+${digits}`,
      };
    }
  }
  return null;
}

/** E.164: + followed by 10–15 digits (no leading zero). */
const E164_REGEX = /^\+[1-9]\d{9,14}$/;

export function isValidPhoneNumber(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  const withPlus = value.trim().startsWith('+') ? value.trim() : `+${digits}`;
  if (!E164_REGEX.test(withPlus)) {
    return false;
  }
  const normalized = withPlus.replace(/\D/g, '');
  return normalized.length >= 10 && normalized.length <= 15;
}
